import React from "react";

import { faCalendarDay } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment, { type Moment } from "moment";

import { capitalizeFirstLetter } from "~/lib/utils";
import { recurrenceRuleToString } from "~/stream/utils";

import type { RowProps } from "./index";

import styles from "./ViewEvent.module.css";

function EventTime({
	start,
	end,
	isAllDay,
	isHappeningNow,
}: {
	start: Moment;
	end: Moment;
	isAllDay: boolean;
	isHappeningNow: boolean;
}) {
	// For in progress events, need to update every minute
	const [_renderKey, setRenderKey] = React.useState(0);
	const timerRef = React.useRef(0);

	const now = moment();
	const startAmPm = start.format("A");
	const endAmPm = end.format("A");
	const startTime = start.minutes() === 0 ? start.format("h") : start.format("h:mm");
	const startSuffix = startAmPm === endAmPm ? "" : ` ${startAmPm}`;
	const endTime = end.minutes() === 0 ? end.format("h") : end.format("h:mm");
	const timeString = `${startTime}${startSuffix} – ${endTime} ${endAmPm}`;

	let eventDate;

	if (start.isSame(now, "day")) {
		eventDate = "Today";
	} else if (start.isSame(moment().subtract(1, "days"), "day")) {
		eventDate = "Yesterday";
	} else if (start.isSame(moment().add(1, "days"), "day")) {
		eventDate = "Tomorrow";
	} else if (start.diff(now, "months") >= 6) {
		eventDate = start.format("MMM D, YYYY");
	} else {
		eventDate = start.format("MMM DD");
	}

	const timerTick = React.useCallback(() => {
		setRenderKey(prev => prev + 1);
	}, []);

	React.useEffect(() => () => window.clearTimeout(timerRef.current), []);

	React.useEffect(() => {
		if (isHappeningNow) {
			timerRef.current = window.setTimeout(timerTick, 60000 - (Date.now() % 60000));
		} else if (timerRef.current) {
			window.clearTimeout(timerRef.current);
		}
	}, [isHappeningNow, timerTick]);

	if (isHappeningNow) {
		const remainingMinutes = end.diff(now, "minutes");
		const remainingHours = Math.floor(remainingMinutes / 60);
		const remainingDays = Math.floor(remainingHours / 24);
		const shouldWrapTime = timeString.length > 14;

		return (
			<>
				<div className={styles.timesRow}>
					<span className={styles.nowText}>Happening now</span>
					{!isAllDay && (
						<>
							<span className={styles.secondaryText}> from </span>
							{shouldWrapTime && <br />}
							<span className={styles.nowText}>{timeString}</span>
						</>
					)}
				</div>

				<div className={cx(styles.textSmall, styles.secondaryText)}>
					{remainingDays > 0 && <span>{remainingDays} days </span>}
					{remainingHours > 0 && <span>{remainingHours} hours </span>}
					{remainingMinutes > 0 && <span>{remainingMinutes % 60} min </span>}
					remaining
				</div>
			</>
		);
	}

	const shouldWrapTime = eventDate.length + timeString.length > 26;

	return (
		<div className={styles.timesRow}>
			<span className={styles.boldTextPrimaryColor}>{eventDate}</span>
			{!isAllDay && (
				<>
					<span className={styles.secondaryText}> from </span>
					{shouldWrapTime && <br />}
					<span className={styles.boldTextPrimaryColor}>{timeString}</span>
				</>
			)}
		</div>
	);
}

export default function EventTimeRow({ event, eventData }: RowProps) {
	const start = moment(event.start);
	const end = moment(event.end);
	const isCancelled = eventData.status === "cancelled";
	const isHappeningNow = !isCancelled && moment().isBetween(start, end);

	return (
		<div className={styles.infoRow}>
			<div className={styles.infoRow__icon}>
				<FontAwesomeIcon fixedWidth icon={faCalendarDay} style={{ marginTop: "3px" }} />
			</div>
			<div className={styles.infoRow__text}>
				<EventTime start={start} end={end} isAllDay={event.isAllDay} isHappeningNow={isHappeningNow} />
				{isCancelled ? (
					<div className={cx(styles.textSmall, styles.cancelledText)}>Cancelled</div>
				) : (
					eventData?.recurrenceRule &&
					!isHappeningNow && (
						<div className={cx(styles.textSmall, styles.secondaryText)}>
							{capitalizeFirstLetter(recurrenceRuleToString(eventData.recurrenceRule))}
						</div>
					)
				)}
			</div>
		</div>
	);
}
